import React, { useState, useEffect } from "react";
import { TextField, Box } from "@material-ui/core";
import { Form, Field } from "react-final-form";
import createDecorator from "final-form-focus";
import { ScheduleGroupCodeScreener } from "./ScheduleGroupCodeScreener";
import { useHeader } from "../../../contexts";
import { IntroText, FormActionDrawer, ButtonLink } from "../../../components";
import { string, object } from "yup";
import { useYupValidation } from "../../../hooks";
import { useTranslation } from "react-i18next";
import {
  GROUP_CODE_DEFAULT,
  GROUP_CODE_DEFAULT_MX,
  COUNTRIES_ISO_CODES
} from "../../../constants";

const focusOnErrors = createDecorator();

const validationSchema = object({
  group_code: string()
    .required()
    .trim()
    .label("Group code")
});

const ScheduleGroupCode = ({
  setDrawerClearance,
  handleSubmit,
  loading = false,
  countryCode = COUNTRIES_ISO_CODES.US
}) => {
  const { t } = useTranslation();

  const { setTitle } = useHeader();
  const { validate } = useYupValidation();

  const [screenerResponse, setScreenerResponse] = useState();
  const [answeredScreener, setAnsweredScreener] = useState(false);

  useEffect(() => {
    setTitle(t("schedule.group_code.title"));
  }, []);

  const getGroupCodeForCountry = countryCode => {
    switch (countryCode.toUpperCase()) {
      case COUNTRIES_ISO_CODES.MX:
        return GROUP_CODE_DEFAULT_MX;
      default:
        return GROUP_CODE_DEFAULT;
    }
  };

  useEffect(() => {
    if (!screenerResponse) return;

    if (screenerResponse === "yes") {
      setAnsweredScreener(true);
    } else {
      handleSubmit(getGroupCodeForCountry(countryCode));
    }
  }, [screenerResponse]);

  /**
   * Handle submit
   *
   * @param {object} data
   */
  const onSubmit = async data => {
    const errors = await validate(validationSchema)(data);

    if (errors) return errors;

    handleSubmit(data.group_code);
  };

  const handleNoCode = () => handleSubmit(getGroupCodeForCountry(countryCode));

  return (
    <>
      {!answeredScreener ? (
        <ScheduleGroupCodeScreener
          handleResponse={setScreenerResponse}
          isLoading={loading}
        />
      ) : (
        <>
          <Form
            onSubmit={onSubmit}
            initialValues={{}}
            decorators={[focusOnErrors]}
            render={({ handleSubmit, submitErrors: errors = {} }) => (
              <form onSubmit={handleSubmit}>
                <IntroText>{t("schedule.group_code.title")}</IntroText>
                <Field
                  name="group_code"
                  render={({ input }) => (
                    <TextField
                      {...input}
                      label={t("schedule.group_code.form.group_code.label")}
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      error={!!errors.group_code}
                      helperText={errors.group_code}
                      inputProps={{
                        "data-lpignore": "true",
                        "data-cy": "input-group_code"
                      }}
                      autoFocus
                    />
                  )}
                />
                <FormActionDrawer
                  setDrawerClearance={setDrawerClearance}
                  loading={loading}
                />
              </form>
            )}
          />
          <Box textAlign="center">
            <ButtonLink
              onClick={e => handleNoCode()}
              color="inherit"
              label={t("schedule.group_code.options.no_code")}
            />
          </Box>
        </>
      )}
    </>
  );
};

export { ScheduleGroupCode };
